import { defaultRichmondLocation } from '../components/Map/MapUtilities';
import { AreaType, ElasticGeoPoint, GeoPoint, LocationOrigin, SearchType, UserLocation, UserSearch } from '../interfaces/interfaces';
import callIpStack from '../services/location/locationIpStack';
import { geocodeByAddress as googleApiGeocodeByAddress } from 'react-places-autocomplete';
import normalizeGeocodeResults from '../services/location/locationGoogleGeocodeResult';
import { getSinglePropertyFromElasticSearch } from '../services/search/searchElastic';
import { normalizeGeoPoint } from './util';

async function fetchLocationViaIpAddress(): Promise<UserLocation> {
	let userLocation: UserLocation = { ...defaultRichmondLocation, initialized: true };
	try {
		const ipStackLocation = await callIpStack();

		if (ipStackLocation) {
			userLocation = {
				initialized: true,
				areaType: AreaType.GeoIp,
				zoomLevel: 12,
				centerMap: { lat: ipStackLocation?.latitude, lng: ipStackLocation?.longitude },
				state: ipStackLocation?.region_code,
				city: ipStackLocation?.city,
				zip: ipStackLocation?.zip,
				origin: LocationOrigin.CurrentLocation
			};
		} else {
			console.warn('Failed to get IP address location - Setting location to main default Richmond, VA: ', userLocation);
		}
		return userLocation;
	}
	catch (error: any) {
		console.error('Problem getting location (LCH.fetchByIp)', error);
		return userLocation;
	}
}


export async function getLocationFromSearch(userSearch: UserSearch): Promise<UserLocation> {
	const fetchLocationViaSearch = async (userSearch: UserSearch): Promise<UserLocation> => {
		if (!userSearch.currentAACValue)
			throw new Error('No search terms');
		try {
			const googleGeocodeResponse = await googleApiGeocodeByAddress(userSearch.currentAACValue as string);
			const normalizedResults = normalizeGeocodeResults(googleGeocodeResponse);

			return { ...normalizedResults };
		}
		catch (error: any) {
			console.error('Places Autocomplete error in app (CHL): ', error);
			return defaultRichmondLocation;
		}
	};

	const getLocationFromMlsId = async (mlsId: string | undefined): Promise<UserLocation> => {
		if (!mlsId) {
			console.error('Indeterminate Property for MlsId: Cannot Proceed. (CHP)');
			return defaultRichmondLocation;
		}

		try {
			const property = await getSinglePropertyFromElasticSearch('', mlsId);
			const centerPoint = normalizeGeoPoint(property.location);

			return {
				initialized: true,
				areaType: AreaType.MlsId,
				mlsId: mlsId,
				zoomLevel: 17,
				centerMap: centerPoint,
				origin: LocationOrigin.UserSearch
			};

		} catch (error) {
			console.error('Problem fetching property data for Mls Id. (CHP)', error);
			return defaultRichmondLocation;
		}
	};

	// NOTE: Must get a map center point (lat/lng)  to move map to new search area
	// For Search/AAC we get center for Google's Geocode results early to recenter map
	// For Device or IP address location we get the center from the source
	// For MLS Id it is more difficult - need to query Elastic first to get lat/lng
	try {
		switch (userSearch?.searchType) {
			case SearchType.TextInput:
				return await fetchLocationViaSearch(userSearch);
			case SearchType.MlsSearch:
				return await getLocationFromMlsId(userSearch.mlsId);
			default:
				return defaultRichmondLocation;
		}
	}
	catch (error) {
		console.error('Error getting UserSearch location');
		return defaultRichmondLocation;
	}
}

export async function fetchLocationViaDeviceBrowser(isMobile: boolean): Promise<UserLocation> {

	let fsError: any = '';
	const options = { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 };
	let position: any;

	function getPosition(options?: PositionOptions): Promise<any> {
		return new Promise((resolve, reject) =>
			navigator.geolocation.getCurrentPosition(resolve, reject, options)
		);
	}

	try {
		if (navigator.geolocation) {
			if (navigator.permissions) {
				try {
					const navigatorPermissionsResult = await navigator.permissions.query({ name: 'geolocation' });
					switch (navigatorPermissionsResult.state) {
						case 'granted':
							position = await getPosition();
							break;
						case 'prompt':
							position = await getPosition(options);
							break;
						case 'denied':
							fsError = 'Denied by User';
							return await cannotGetLocationFromDevice();
						default:
							fsError = 'No Geo Permission status';
							return { ...defaultRichmondLocation, initialized: true };
					}

				}
				catch (error: any) {
					fsError = ` Problem with device location: ${error.toString()}`;
					return await cannotGetLocationFromDevice();
				}
			} else {
				position = await getPosition(options);
			}
			if (position?.coords?.latitude !== 0) {
				return {
					initialized: true,
					areaType: AreaType.Geopoint,
					centerMap: { lat: position.coords.latitude, lng: position.coords.longitude },
					zoomLevel: 12,
					origin: LocationOrigin.CurrentLocation
				};
			} else return { ...defaultRichmondLocation, initialized: true };

		} else {
			fsError = 'Device/browser does not appear to support navigator.geolocation';
			return await cannotGetLocationFromDevice();
		}
	} catch (e) {
		console.error('navigator error: ', e);
		return await cannotGetLocationFromDevice();
	}

	// Fallback to ip address geolocation (only for desktop)
	async function cannotGetLocationFromDevice(): Promise<UserLocation> {
		if (!isMobile) {
			console.warn(`Fallback to IP. ${fsError ? `Error: ${fsError}` : ''}`);
			return await fetchLocationViaIpAddress();
		}
		else {
			console.warn('Cannot Fallback to IP address location on mobile');
			return { ...defaultRichmondLocation, initialized: true, origin: LocationOrigin.CurrentLocation };
		}
	}
}

export const getElasticGeopoint = (geopoint: GeoPoint): ElasticGeoPoint => ({ lat: geopoint.lat, lon: geopoint.lng });